import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import AnnouncementBar from "@/components/AnnouncementBar";
import TawkTo from "@/components/TawkTo";
import WhatsAppFab from "@/components/WhatsAppFab";
import {
  LayoutDashboard, FileText, Bell, LogOut, User, ShieldCheck,
  ListOrdered, Settings2, Home, Receipt, Users2, Megaphone, BarChart3,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const CUSTOMER_LINKS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/dashboard/orders", label: "My Filings", icon: FileText },
  { to: "/dashboard/invoices", label: "Invoices", icon: Receipt },
  { to: "/dashboard/support", label: "Help & Support", icon: Bell },
  { to: "/dashboard/profile", label: "Profile", icon: User },
];

const ADMIN_LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/orders", label: "All Orders", icon: ListOrdered },
  { to: "/admin/users", label: "Users", icon: Users2 },
  { to: "/admin/services", label: "Services", icon: Settings2 },
  { to: "/admin/coupons", label: "Coupons", icon: Receipt },
  { to: "/admin/reports", label: "GST Reports", icon: BarChart3 },
  { to: "/admin/tickets", label: "Support Tickets", icon: Bell },
  { to: "/admin/content", label: "Content", icon: Megaphone },
];

export default function DashboardLayout({ children, admin = false }) {
  const { user, logout } = useAuth();
  useSiteSettings();
  const nav = useNavigate();
  const links = admin ? ADMIN_LINKS : CUSTOMER_LINKS;
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.notifications.filter((n) => !n.read).length);
      } catch { /* ignore */ }
    };
    load();
    timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {!admin && <AnnouncementBar />}
      {!admin && <TawkTo />}
      {!admin && <WhatsAppFab />}
      <div className="flex flex-col md:flex-row flex-1">
        <aside className="md:w-64 md:min-h-[calc(100vh-40px)] border-r border-slate-200 bg-white flex md:flex-col shrink-0">
        <div className="p-5 border-b border-slate-200 hidden md:block">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex w-9 h-9 rounded-md bg-[#1E3A8A] items-center justify-center text-white font-bold font-display">W2</span>
            <div>
              <div className="font-display font-bold text-[#0A192F]">Way2Efiling</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 -mt-0.5">
                {admin ? "Admin Console" : "Customer"}
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex md:flex-col overflow-x-auto md:overflow-visible flex-1 p-3 gap-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/dashboard" || l.to === "/admin"}
                data-testid={`side-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    isActive
                      ? "bg-[#1E3A8A] text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-[#1E3A8A]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden md:block p-3 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => nav("/")} data-testid="side-home-btn">
              <Home className="w-4 h-4 mr-1" /> Site
            </Button>
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="side-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          {admin && (
            <div className="mt-3 status-badge badge-info w-full justify-center">
              <ShieldCheck className="w-3 h-3" /> Administrator
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-slate-200 bg-white md:hidden px-4 py-3 flex justify-between items-center">
          <Link to="/" className="font-display font-bold text-[#0A192F]">Way2Efiling</Link>
          <Button variant="outline" size="sm" onClick={() => { logout(); nav("/"); }}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
          {children}
        </div>
      </main>
      </div>
      {!admin && <DashboardFooter />}
    </div>
  );
}

function DashboardFooter() {
  const { settings } = useSiteSettings();
  const contact = settings?.contact || {};
  return (
    <footer className="border-t border-slate-200 bg-white text-slate-600 text-xs" data-testid="dashboard-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <div>© {new Date().getFullYear()} Way2Efiling · SHREE ADARSH INFOTECH · GSTIN 08KKOPS0455P1Z2</div>
        <div className="flex items-center gap-3">
          {contact.email && <a href={`mailto:${contact.email}`} className="hover:text-[#1E3A8A]">{contact.email}</a>}
          {contact.phone && <a href={`tel:${contact.phone}`} className="hover:text-[#1E3A8A]">{contact.phone}</a>}
          <Link to="/pages/privacy-policy" className="hover:text-[#1E3A8A]">Privacy</Link>
          <Link to="/pages/terms" className="hover:text-[#1E3A8A]">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
